import React, { useState, useEffect, useRef } from 'react';
import { 
  Workflow, 
  Plus, 
  Trash2, 
  Settings, 
  Play, 
  Save, 
  Download, 
  Upload, 
  Copy, 
  Eye, 
  EyeOff,
  Zap,
  GitBranch,
  Clock,
  AlertTriangle,
  CheckCircle,
  X,
  ArrowRight,
  RotateCcw,
  Maximize,
  Minimize
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

interface A2AWorkflowDesignerProps {
  isOpen: boolean;
  onClose: () => void;
  initialWorkflow?: any;
}

interface WorkflowStep {
  id: string;
  type: 'agent_task' | 'condition' | 'transformation' | 'human_input' | 'external_api' | 'parallel_execution' | 'dynamic_routing';
  label: string;
  config: any;
  position: { x: number; y: number };
  nextSuccess?: string;
  nextFailure?: string;
  status?: 'idle' | 'running' | 'completed' | 'failed';
}

interface WorkflowConnection {
  id: string;
  source: string;
  target: string;
  type: 'success' | 'failure';
}

interface WorkflowDefinition {
  name: string;
  description: string;
  steps: WorkflowStep[];
  connections: WorkflowConnection[];
  is_template?: boolean;
  is_public?: boolean;
}

const STEP_TYPES = [
  {
    id: 'agent_task',
    name: 'Agent Task',
    description: 'Delegate work to a specific agent',
    icon: Zap,
    color: 'bg-blue-500'
  },
  {
    id: 'condition',
    name: 'Condition',
    description: 'Evaluate expressions to determine flow',
    icon: GitBranch,
    color: 'bg-green-500'
  },
  {
    id: 'transformation',
    name: 'Transformation',
    description: 'Transform data between steps',
    icon: RotateCcw,
    color: 'bg-purple-500'
  },
  {
    id: 'parallel_execution',
    name: 'Parallel Execution',
    description: 'Execute multiple branches simultaneously',
    icon: GitBranch,
    color: 'bg-orange-500'
  },
  {
    id: 'dynamic_routing',
    name: 'Dynamic Routing',
    description: 'Select agent based on real-time criteria',
    icon: ArrowRight,
    color: 'bg-red-500'
  },
  {
    id: 'human_input',
    name: 'Human Input',
    description: 'Request input from a user',
    icon: Clock,
    color: 'bg-yellow-500'
  },
  {
    id: 'external_api',
    name: 'External API',
    description: 'Call external services',
    icon: Settings,
    color: 'bg-gray-500'
  }
];

export function A2AWorkflowDesigner({ isOpen, onClose, initialWorkflow }: A2AWorkflowDesignerProps) {
  const [workflow, setWorkflow] = useState<WorkflowDefinition>({
    name: 'New Workflow',
    description: 'A new multi-agent workflow',
    steps: [],
    connections: [],
    is_template: false,
    is_public: false
  });
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [draggedStepType, setDraggedStepType] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState<any>(null);
  const [showStepConfig, setShowStepConfig] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<any[]>([]);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      fetchAvailableAgents();
      if (initialWorkflow) {
        setWorkflow(initialWorkflow);
      }
    }
  }, [isOpen, initialWorkflow]);

  const fetchAvailableAgents = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/discover`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({})
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableAgents(data.agents || []);
        }
      }
    } catch (error) {
      console.error('Error fetching available agents:', error);
    }
  };

  const addStep = (stepType: string, position: { x: number; y: number }) => {
    const newStep: WorkflowStep = {
      id: `step_${Date.now()}`,
      type: stepType as any,
      label: `${STEP_TYPES.find(t => t.id === stepType)?.name || stepType}`,
      config: getDefaultStepConfig(stepType),
      position,
      status: 'idle'
    };

    setWorkflow(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));

    setSelectedStep(newStep.id);
    setShowStepConfig(true);
  };

  const getDefaultStepConfig = (stepType: string): any => {
    switch (stepType) {
      case 'agent_task':
        return {
          agent_id: '',
          action: 'process',
          input_mapping: {},
          output_mapping: {},
          timeout: 30000,
          retries: 3,
          retry_delay: 1000
        };
      case 'condition':
        return {
          condition_type: 'javascript',
          condition: 'data.value > 0',
          path_true: '',
          path_false: ''
        };
      case 'transformation':
        return {
          transformation_type: 'map',
          transformation_config: {
            mapping: {}
          }
        };
      case 'parallel_execution':
        return {
          branches: [],
          merge_strategy: 'all_complete',
          timeout: 60000
        };
      case 'dynamic_routing':
        return {
          routing_criteria: 'load_balancing',
          agent_pool: [],
          selection_strategy: 'round_robin'
        };
      case 'human_input':
        return {
          prompt: 'Please provide input',
          input_type: 'text',
          timeout: 300000
        };
      case 'external_api':
        return {
          url: '',
          method: 'POST',
          headers: {},
          body_template: {},
          timeout: 30000
        };
      default:
        return {};
    }
  };

  const updateStepConfig = (stepId: string, config: any) => {
    setWorkflow(prev => ({
      ...prev,
      steps: prev.steps.map(step => 
        step.id === stepId ? { ...step, config } : step
      )
    }));
  };

  const deleteStep = (stepId: string) => {
    setWorkflow(prev => ({
      ...prev,
      steps: prev.steps.filter(step => step.id !== stepId),
      connections: prev.connections.filter(conn => 
        conn.source !== stepId && conn.target !== stepId
      )
    }));
    
    if (selectedStep === stepId) {
      setSelectedStep(null);
      setShowStepConfig(false);
    }
  };

  const connectSteps = (sourceId: string, targetId: string, connectionType: 'success' | 'failure') => {
    const connectionId = `conn_${Date.now()}`;
    
    setWorkflow(prev => ({
      ...prev,
      connections: [...prev.connections, {
        id: connectionId,
        source: sourceId,
        target: targetId,
        type: connectionType
      }]
    }));
  };

  const executeWorkflow = async () => {
    if (!workflow.steps.length) {
      alert('Please add at least one step to the workflow');
      return;
    }

    setIsExecuting(true);
    setExecutionResults(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-workflow-orchestrator`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'execute',
            workflow_definition: workflow,
            input_data: { test: true },
            user_id: user?.id
          })
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setExecutionResults(data);
        // Poll for execution status
        pollExecutionStatus(data.execution_id);
      } else {
        throw new Error(data.error || 'Workflow execution failed');
      }
    } catch (error) {
      console.error('Error executing workflow:', error);
      alert(`Execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExecuting(false);
    }
  };

  const pollExecutionStatus = async (executionId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-workflow-orchestrator`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'get_status',
              execution_id: executionId
            })
          }
        );

        const data = await response.json();
        
        if (data.success) {
          setExecutionResults(data);
          
          // Stop polling if execution is complete
          if (data.execution.status === 'completed' || data.execution.status === 'failed') {
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('Error polling execution status:', error);
        clearInterval(pollInterval);
      }
    }, 2000);
  };

  const saveWorkflow = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-workflow-orchestrator`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'create',
            workflow_definition: workflow,
            user_id: user?.id
          })
        }
      );

      const data = await response.json();
      
      if (data.success) {
        alert('Workflow saved successfully!');
      } else {
        throw new Error(data.error || 'Failed to save workflow');
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      alert(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const renderStepConfigPanel = () => {
    const currentStep = workflow.steps.find(s => s.id === selectedStep);
    if (!currentStep) return null;

    return (
      <div className="w-80 bg-white border-l border-gray-200 p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Configure Step</h3>
          <button
            onClick={() => {
              setShowStepConfig(false);
              setSelectedStep(null);
            }}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Step Label</label>
            <input
              type="text"
              value={currentStep.label}
              onChange={(e) => {
                setWorkflow(prev => ({
                  ...prev,
                  steps: prev.steps.map(step => 
                    step.id === currentStep.id ? { ...step, label: e.target.value } : step
                  )
                }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {currentStep.type === 'agent_task' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Agent</label>
                <select
                  value={currentStep.config.agent_id || ''}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, agent_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Choose an agent...</option>
                  {availableAgents.map(agent => (
                    <option key={agent.agent_id} value={agent.agent_id}>
                      {agent.name} ({agent.type || 'internal'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <input
                  type="text"
                  value={currentStep.config.action || ''}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, action: e.target.value })}
                  placeholder="process, analyze, generate..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (ms)</label>
                <input
                  type="number"
                  value={currentStep.config.timeout || 30000}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, timeout: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Retries</label>
                <input
                  type="number"
                  value={currentStep.config.retries || 3}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, retries: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          )}

          {currentStep.type === 'condition' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condition Type</label>
                <select
                  value={currentStep.config.condition_type || 'javascript'}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, condition_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="javascript">JavaScript Expression</option>
                  <option value="ai_powered">AI-Powered Evaluation</option>
                  <option value="regex">Regular Expression</option>
                  <option value="json_path">JSON Path</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                <textarea
                  value={currentStep.config.condition || ''}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, condition: e.target.value })}
                  placeholder="data.value > 100"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>
            </div>
          )}

          {currentStep.type === 'transformation' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transformation Type</label>
                <select
                  value={currentStep.config.transformation_type || 'map'}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, transformation_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="map">Map</option>
                  <option value="filter">Filter</option>
                  <option value="reduce">Reduce</option>
                  <option value="ai_transform">AI-Powered Transform</option>
                  <option value="merge">Merge</option>
                  <option value="split">Split</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Configuration</label>
                <textarea
                  value={JSON.stringify(currentStep.config.transformation_config || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const config = JSON.parse(e.target.value);
                      updateStepConfig(currentStep.id, { ...currentStep.config, transformation_config: config });
                    } catch (error) {
                      // Invalid JSON, don't update
                    }
                  }}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>
            </div>
          )}

          {currentStep.type === 'parallel_execution' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Merge Strategy</label>
                <select
                  value={currentStep.config.merge_strategy || 'all_complete'}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, merge_strategy: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all_complete">Wait for All</option>
                  <option value="first_complete">First to Complete</option>
                  <option value="majority_complete">Majority Complete</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (ms)</label>
                <input
                  type="number"
                  value={currentStep.config.timeout || 60000}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, timeout: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          )}

          {currentStep.type === 'dynamic_routing' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Routing Criteria</label>
                <select
                  value={currentStep.config.routing_criteria || 'load_balancing'}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, routing_criteria: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="load_balancing">Load Balancing</option>
                  <option value="capability_match">Best Capability Match</option>
                  <option value="performance">Best Performance</option>
                  <option value="cost_optimization">Cost Optimization</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selection Strategy</label>
                <select
                  value={currentStep.config.selection_strategy || 'round_robin'}
                  onChange={(e) => updateStepConfig(currentStep.id, { ...currentStep.config, selection_strategy: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="round_robin">Round Robin</option>
                  <option value="weighted">Weighted Selection</option>
                  <option value="random">Random</option>
                  <option value="priority">Priority Based</option>
                </select>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={() => deleteStep(currentStep.id)}
              className="w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              Delete Step
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCanvas = () => {
    return (
      <div 
        ref={canvasRef}
        className="flex-1 relative overflow-hidden bg-gray-50"
        style={{ transform: `scale(${zoom}) translate(${panOffset.x}px, ${panOffset.y}px)` }}
        onDrop={(e) => {
          e.preventDefault();
          if (draggedStepType) {
            const rect = canvasRef.current?.getBoundingClientRect();
            if (rect) {
              const x = (e.clientX - rect.left) / zoom - panOffset.x;
              const y = (e.clientY - rect.top) / zoom - panOffset.y;
              addStep(draggedStepType, { x, y });
            }
            setDraggedStepType(null);
          }
        }}
        onDragOver={(e) => e.preventDefault()}
      >
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Connections */}
        <svg className="absolute inset-0 pointer-events-none">
          {workflow.connections.map(connection => {
            const sourceStep = workflow.steps.find(s => s.id === connection.source);
            const targetStep = workflow.steps.find(s => s.id === connection.target);
            
            if (!sourceStep || !targetStep) return null;
            
            const sourceX = sourceStep.position.x + 100;
            const sourceY = sourceStep.position.y + 40;
            const targetX = targetStep.position.x;
            const targetY = targetStep.position.y + 40;
            
            return (
              <g key={connection.id}>
                <path
                  d={`M ${sourceX} ${sourceY} Q ${sourceX + 50} ${sourceY} ${targetX} ${targetY}`}
                  stroke={connection.type === 'success' ? '#10b981' : '#ef4444'}
                  strokeWidth="2"
                  fill="none"
                  markerEnd="url(#arrowhead)"
                />
              </g>
            );
          })}
          
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
            </marker>
          </defs>
        </svg>

        {/* Steps */}
        {workflow.steps.map(workflowStep => {
          const stepType = STEP_TYPES.find(t => t.id === workflowStep.type);
          const isSelected = selectedStep === workflowStep.id;
          
          return (
            <div
              key={workflowStep.id}
              className={`absolute w-48 bg-white border-2 rounded-lg p-3 cursor-pointer transition-all ${
                isSelected ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'
              }`}
              style={{
                left: workflowStep.position.x,
                top: workflowStep.position.y,
                transform: `scale(${1/zoom})`
              }}
              onClick={() => {
                setSelectedStep(workflowStep.id);
                setShowStepConfig(true);
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1 ${stepType?.color || 'bg-gray-500'} rounded text-white`}>
                  {stepType?.icon && <stepType.icon className="w-4 h-4" />}
                </div>
                <span className="font-medium text-sm text-gray-900">{workflowStep.label}</span>
              </div>
              
              <div className="text-xs text-gray-600 mb-2">
                {stepType?.description}
              </div>
              
              {workflowStep.status && (
                <div className={`text-xs px-2 py-1 rounded ${
                  workflowStep.status === 'completed' ? 'bg-green-100 text-green-700' :
                  workflowStep.status === 'failed' ? 'bg-red-100 text-red-700' :
                  workflowStep.status === 'running' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {workflowStep.status}
                </div>
              )}
              
              <div className="flex justify-between mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Add success connection logic here
                  }}
                  className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200"
                >
                  Success
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Add failure connection logic here
                  }}
                  className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                >
                  Failure
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-7xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Workflow className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">A2A Workflow Designer</h2>
              <p className="text-sm opacity-90">Visual multi-agent workflow creation</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={saveWorkflow}
              className="flex items-center gap-2 px-3 py-1 bg-white/20 rounded-lg text-sm hover:bg-white/30"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={executeWorkflow}
              disabled={isExecuting}
              className="flex items-center gap-2 px-3 py-1 bg-white/20 rounded-lg text-sm hover:bg-white/30 disabled:opacity-50"
            >
              {isExecuting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Execute
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Step Palette */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-4">Step Types</h3>
            <div className="space-y-2">
              {STEP_TYPES.map(stepType => (
                <div
                  key={stepType.id}
                  draggable
                  onDragStart={() => setDraggedStepType(stepType.id)}
                  className="p-3 bg-white border border-gray-200 rounded-lg cursor-move hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`p-1 ${stepType.color} rounded text-white`}>
                      <stepType.icon className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-sm">{stepType.name}</span>
                  </div>
                  <p className="text-xs text-gray-600">{stepType.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Canvas */}
          {renderCanvas()}

          {/* Step Configuration Panel */}
          {showStepConfig && renderStepConfigPanel()}
        </div>

        {/* Execution Results */}
        {executionResults && (
          <div className="border-t border-gray-200 p-4 bg-gray-50 max-h-48 overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-2">Execution Results</h3>
            <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto">
              {JSON.stringify(executionResults, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}